import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    CloseIcon as X,
    CheckCircleIcon as CheckCircle,
    ArrowRightIcon as ArrowRight,
    ArrowLeftIcon as ArrowLeft,
    CalendarIcon as Calendar,
    ClockIcon as Clock,
    LocationOnIcon as Location,
    GroupsIcon as Users,
    CheckIcon,
    AlertCircleIcon,
    InfoIcon as Info
} from './Icons';

const WEEKDAYS = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const WEEKDAY_NAMES = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

const getGroupAgeRange = (group) => {
    const currentYear = new Date().getFullYear();
    const stufe = (group.altersstufe || '').toLowerCase();

    if (group.birthYearStart && group.birthYearEnd) {
        const minYear = Math.min(group.birthYearStart, group.birthYearEnd);
        const maxYear = Math.max(group.birthYearStart, group.birthYearEnd);
        return {
            minAge: currentYear - maxYear,
            maxAge: currentYear - minYear,
            label: `${currentYear - maxYear}–${currentYear - minYear} Jahre`
        };
    }

    if (stufe.includes('wölfling') || stufe.includes('meute') || stufe.includes('biber')) {
        return { minAge: 6, maxAge: 10, label: '6–10 Jahre' };
    }
    if (stufe.includes('jungpfadfinder') || stufe.includes('jupfi')) {
        return { minAge: 10, maxAge: 13, label: '10–13 Jahre' };
    }
    if (stufe.includes('pfadfinder') || stufe.includes('sippe')) {
        return { minAge: 13, maxAge: 16, label: '13–16 Jahre' };
    }
    if (stufe.includes('rover')) {
        return { minAge: 16, maxAge: 25, label: '16+ Jahre' };
    }
    return { minAge: 6, maxAge: 18, label: 'Altersgruppe nach Absprache' };
};

const GroupInquiryWizard = ({ isOpen, onClose, initialGroupId = null }) => {
    const [step, setStep] = useState(1);
    const [loadingGroups, setLoadingGroups] = useState(true);
    const [allGroups, setAllGroups] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');

    // Formular-Felder
    const [childFirstName, setChildFirstName] = useState('');
    const [childLastName, setChildLastName] = useState('');
    const [birthDate, setBirthDate] = useState('');
    const [age, setAge] = useState('');
    const [gender, setGender] = useState('gemischt');
    const [availableDays, setAvailableDays] = useState([]);
    const [preferredGroupIds, setPreferredGroupIds] = useState(initialGroupId ? [initialGroupId] : []);
    const [isWaitlistRequested, setIsWaitlistRequested] = useState(false);

    const [parentName, setParentName] = useState('');
    const [parentEmail, setParentEmail] = useState('');
    const [parentPhone, setParentPhone] = useState('');
    const [street, setStreet] = useState('');
    const [houseNumber, setHouseNumber] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [city, setCity] = useState('');
    const [notes, setNotes] = useState('');

    const apiUrl = (process.env.REACT_APP_API_URL || '/api').replace(/\/+$/, '');

    useEffect(() => {
        if (!isOpen) return;
        const fetchGroups = async () => {
            setLoadingGroups(true);
            try {
                const { data } = await axios.get(`${apiUrl}/groups`);
                setAllGroups(Array.isArray(data) ? data.filter(g => g.isActive !== false) : []);
            } catch (err) {
                console.error('Fehler beim Laden der Gruppen:', err);
            } finally {
                setLoadingGroups(false);
            }
        };
        fetchGroups();
    }, [isOpen, apiUrl]);

    // Berechne Alter automatisch bei Geburtsdatum-Änderung
    const handleBirthDateChange = (e) => {
        const val = e.target.value;
        setBirthDate(val);
        if (val) {
            const bDate = new Date(val);
            const diffMs = Date.now() - bDate.getTime();
            const calcAge = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 365.25));
            if (!isNaN(calcAge) && calcAge >= 0 && calcAge <= 30) {
                setAge(String(calcAge));
            }
        }
    };

    const toggleAvailableDay = (day) => {
        setAvailableDays(prev =>
            prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
        );
    };

    const toggleGroupPreference = (groupId) => {
        setPreferredGroupIds(prev =>
            prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
        );
    };

    // Filterlogik für passende Gruppen
    const currentAgeNumber = age ? parseInt(age, 10) : null;
    const matchingGroups = allGroups.filter(group => {
        if (currentAgeNumber === null) return true;
        const { minAge, maxAge } = getGroupAgeRange(group);
        const ageMatch = currentAgeNumber >= minAge && currentAgeNumber <= maxAge;

        // Geschlechtsprüfung (sofern Gruppe nicht gemischt ist)
        const groupGender = group.genderFocus || 'gemischt';
        const genderMatch = groupGender === 'gemischt' || gender === 'divers' || groupGender === gender;

        return ageMatch && genderMatch;
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setSubmitError('');

        try {
            const finalNotes = isWaitlistRequested
                ? (notes ? `[Warteliste] ${notes}` : '[Warteliste]')
                : notes;

            await axios.post(`${apiUrl}/inquiries`, {
                childFirstName,
                childLastName,
                birthDate: birthDate || undefined,
                age: currentAgeNumber,
                gender,
                parentName,
                parentEmail,
                parentPhone,
                street,
                houseNumber,
                zipCode,
                city,
                availableDays,
                preferredGroupIds,
                notes: finalNotes
            });

            setSubmitSuccess(true);
        } catch (err) {
            console.error('Fehler beim Absenden:', err);
            setSubmitError(err.response?.data?.message || 'Fehler beim Übermitteln der Anfrage. Bitte versuche es erneut.');
        } finally {
            setSubmitting(false);
        }
    };

    const resetAndClose = () => {
        setStep(1);
        setSubmitSuccess(false);
        setSubmitError('');
        setChildFirstName('');
        setChildLastName('');
        setBirthDate('');
        setAge('');
        setGender('gemischt');
        setAvailableDays([]);
        setPreferredGroupIds([]);
        setIsWaitlistRequested(false);
        setParentName('');
        setParentEmail('');
        setParentPhone('');
        setStreet('');
        setHouseNumber('');
        setZipCode('');
        setCity('');
        setNotes('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
            <div className="md3-card-elevated w-full max-w-2xl max-h-[90vh] flex flex-col bg-surface rounded-3xl shadow-elevation-5 border border-outlineVariant/40 overflow-hidden">
                
                {/* Header */}
                <div className="p-6 border-b border-outlineVariant/30 flex items-center justify-between bg-surface-containerLow">
                    <div>
                        <span className="text-xs font-bold text-primary uppercase tracking-wider block">
                            Interessenbekundung & Warteliste
                        </span>
                        <h2 className="text-title-large font-bold text-surface-on">
                            {submitSuccess ? 'Anfrage erhalten' : `Schritt ${step} von 3: ${
                                step === 1 ? 'Angaben zum Kind & Verfügbarkeit' :
                                step === 2 ? 'Gruppen-Möglichkeiten & Favoriten' :
                                'Kontaktdaten der Eltern'
                            }`}
                        </h2>
                    </div>
                    <button
                        onClick={resetAndClose}
                        className="p-2 text-surface-onVariant hover:text-surface-on rounded-full hover:bg-surface-containerHigh transition-colors"
                        title="Schließen"
                    >
                        <X style={{ fontSize: 22 }} />
                    </button>
                </div>

                {/* Progress Bar */}
                {!submitSuccess && (
                    <div className="w-full bg-surface-containerHigh h-1">
                        <div
                            className="bg-primary h-1 transition-all duration-300"
                            style={{ width: `${(step / 3) * 100}%` }}
                        />
                    </div>
                )}

                {/* Content Body */}
                <div className="p-6 overflow-y-auto flex-1 text-surface-on text-body-medium">
                    {submitSuccess ? (
                        <div className="py-8 text-center space-y-4">
                            <div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
                                <CheckCircle style={{ fontSize: 40 }} />
                            </div>
                            <h3 className="text-headline-small font-bold text-surface-on">
                                Vielen Dank für deine Interessenbekundung!
                            </h3>
                            <p className="text-body-large text-surface-onVariant max-w-md mx-auto leading-relaxed">
                                Wir haben deine Angaben für <strong>{childFirstName} {childLastName}</strong> erfolgreich gespeichert.
                            </p>
                            <div className="p-4 rounded-2xl bg-surface-containerLow border border-outlineVariant/40 max-w-md mx-auto text-left text-sm text-surface-onVariant space-y-2">
                                <p className="font-semibold text-surface-on">Wie geht es jetzt weiter?</p>
                                <ul className="list-disc list-inside space-y-1 text-xs">
                                    <li>Unsere Gruppenleitungen prüfen aktuell die freien Plätze bzw. sammeln Anfragen für Neugründungen.</li>
                                    <li>Du hast eine Eingangsbestätigung an <strong>{parentEmail}</strong> erhalten.</li>
                                    <li>Wir melden uns zeitnah persönlich bei dir, um Termine oder Schnupperstunden abzusprechen.</li>
                                </ul>
                            </div>
                            <div className="pt-4">
                                <button
                                    onClick={resetAndClose}
                                    className="md3-btn-filled px-8 py-2.5 rounded-full"
                                >
                                    Fertig
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            
                            {/* SCHRITT 1: Kind & Zeit */}
                            {step === 1 && (
                                <div className="space-y-5 animate-fade-in">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-label-medium font-semibold text-surface-on mb-1.5">
                                                Vorname des Kindes *
                                            </label>
                                            <input
                                                type="text"
                                                required
                                                value={childFirstName}
                                                onChange={(e) => setChildFirstName(e.target.value)}
                                                placeholder="z. B. Max"
                                                className="w-full px-4 py-2.5 rounded-xl border border-outlineVariant bg-surface-containerLowest text-surface-on focus:border-primary focus:outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-label-medium font-semibold text-surface-on mb-1.5">
                                                Nachname des Kindes *
                                            </label>
                                            <input
                                                type="text"
                                                required
                                                value={childLastName}
                                                onChange={(e) => setChildLastName(e.target.value)}
                                                placeholder="z. B. Mustermann"
                                                className="w-full px-4 py-2.5 rounded-xl border border-outlineVariant bg-surface-containerLowest text-surface-on focus:border-primary focus:outline-none"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-label-medium font-semibold text-surface-on mb-1.5">
                                                Geburtsdatum
                                            </label>
                                            <input
                                                type="date"
                                                value={birthDate}
                                                onChange={handleBirthDateChange}
                                                className="w-full px-4 py-2.5 rounded-xl border border-outlineVariant bg-surface-containerLowest text-surface-on focus:border-primary focus:outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-label-medium font-semibold text-surface-on mb-1.5">
                                                Alter (Jahre) *
                                            </label>
                                            <input
                                                type="number"
                                                required
                                                min="4"
                                                max="25"
                                                value={age}
                                                onChange={(e) => setAge(e.target.value)}
                                                placeholder="z. B. 8"
                                                className="w-full px-4 py-2.5 rounded-xl border border-outlineVariant bg-surface-containerLowest text-surface-on focus:border-primary focus:outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-label-medium font-semibold text-surface-on mb-1.5">
                                                Geschlecht
                                            </label>
                                            <select
                                                value={gender}
                                                onChange={(e) => setGender(e.target.value)}
                                                className="w-full px-4 py-2.5 rounded-xl border border-outlineVariant bg-surface-containerLowest text-surface-on focus:border-primary focus:outline-none"
                                            >
                                                <option value="männlich">männlich</option>
                                                <option value="weiblich">weiblich</option>
                                                <option value="divers">divers / keine Angabe</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-label-medium font-semibold text-surface-on mb-1">
                                            An welchen Tagen hätte das Kind grundsätzlich Zeit für Gruppenstunden?
                                        </label>
                                        <p className="text-xs text-surface-onVariant mb-3">
                                            Wähle alle Wochentage aus, die für euch in Frage kommen.
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            {WEEKDAYS.map(day => {
                                                const isSelected = availableDays.includes(day);
                                                return (
                                                    <button
                                                        key={day}
                                                        type="button"
                                                        onClick={() => toggleAvailableDay(day)}
                                                        className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors flex items-center gap-1.5 ${
                                                            isSelected
                                                                ? 'bg-primary text-primary-on border-primary'
                                                                : 'bg-surface-containerLowest text-surface-on border-outlineVariant/50 hover:bg-surface-container'
                                                        }`}
                                                    >
                                                        {isSelected && <CheckIcon style={{ fontSize: 16 }} />}
                                                        <span>{day}</span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* SCHRITT 2: Passende Gruppen & Favoriten */}
                            {step === 2 && (
                                <div className="space-y-5 animate-fade-in">
                                    <div className="p-4 rounded-2xl bg-surface-containerLow border border-outlineVariant/40 text-xs text-surface-onVariant">
                                        <p className="font-semibold text-surface-on mb-1">Unverbindliche Favoritenauswahl</p>
                                        <p>
                                            Nachfolgend siehst du alle Gruppen, die für das Alter ({age} Jahre) und Geschlecht in Frage kommen.
                                            Die Auswahl eines Favoriten gilt noch <strong>nicht als verbindliche Aufnahme</strong>, sondern hilft der Leitung bei der Einteilung.
                                        </p>
                                    </div>

                                    {loadingGroups ? (
                                        <div className="py-12 text-center text-surface-onVariant text-sm">
                                            Lade Gruppeninformationen...
                                        </div>
                                    ) : matchingGroups.length > 0 ? (
                                        <div className="space-y-3">
                                            {/* Prominente Warteliste Option */}
                                            <div
                                                onClick={() => setIsWaitlistRequested(!isWaitlistRequested)}
                                                className={`p-4 rounded-2xl border cursor-pointer transition-all flex items-center justify-between ${
                                                    isWaitlistRequested
                                                        ? 'bg-primary/10 border-primary ring-2 ring-primary/20 shadow-sm'
                                                        : 'bg-surface border-outlineVariant/40 hover:bg-surface-container'
                                                }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isWaitlistRequested ? 'bg-primary text-primary-on' : 'bg-surface-containerHigh text-primary'}`}>
                                                        <Clock style={{ fontSize: 18 }} />
                                                    </span>
                                                    <div>
                                                        <div className="font-bold text-sm text-surface-on flex items-center gap-2">
                                                            <span>Auf die Warteliste setzen</span>
                                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary-container text-primary-onContainer border border-primary/20">Unverbindlich</span>
                                                        </div>
                                                        <div className="text-xs text-surface-onVariant">Gruppe voll oder Zeiten passen noch nicht? Wir merken dein Kind bevorzugt vor.</div>
                                                    </div>
                                                </div>
                                                <div className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${isWaitlistRequested ? 'bg-primary border-primary text-primary-on' : 'border-outlineVariant'}`}>
                                                    {isWaitlistRequested && <CheckIcon style={{ fontSize: 14 }} />}
                                                </div>
                                            </div>

                                            <p className="text-sm font-semibold text-surface-on pt-1">
                                                Gefundene Gruppen ({matchingGroups.length}):
                                            </p>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                {matchingGroups.map(group => {
                                                    const isSelected = preferredGroupIds.includes(group._id);
                                                    const schedule = group.standardSchedule;
                                                    const dayName = schedule?.dayOfWeek !== undefined ? WEEKDAY_NAMES[schedule.dayOfWeek] : (group.meetingTime || 'Termin nach Absprache');
                                                    const timeStr = schedule?.startTime && schedule?.endTime ? `${schedule.startTime} – ${schedule.endTime} Uhr` : '';
                                                    const ageInfo = getGroupAgeRange(group);
                                                    const statusLabel = group.admissionStatus === 'waitlist'
                                                        ? 'Warteliste aktiv'
                                                        : group.admissionStatus === 'closed'
                                                        ? 'Aufnahmestopp'
                                                        : 'Plätze frei';

                                                    return (
                                                        <div
                                                            key={group._id}
                                                            onClick={() => toggleGroupPreference(group._id)}
                                                            className={`p-4 rounded-2xl border cursor-pointer transition-all flex flex-col justify-between ${
                                                                isSelected
                                                                    ? 'bg-primary/5 border-primary ring-2 ring-primary/20 shadow-sm'
                                                                    : 'bg-surface-containerLowest border-outlineVariant/40 hover:border-primary/50'
                                                            }`}
                                                        >
                                                            <div>
                                                                <div className="flex items-center justify-between gap-2 mb-2">
                                                                    <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-surface-containerHigh text-surface-on">
                                                                        {group.altersstufe || 'Gruppe'}
                                                                    </span>
                                                                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                                                                        group.admissionStatus === 'waitlist'
                                                                            ? 'bg-secondary-container text-secondary-onContainer'
                                                                            : group.admissionStatus === 'closed'
                                                                            ? 'bg-error-container text-error-onErrorContainer'
                                                                            : 'bg-primary-container text-primary-onContainer'
                                                                    }`}>
                                                                        {statusLabel}
                                                                    </span>
                                                                </div>
                                                                <h4 className="text-title-medium font-bold text-surface-on mb-1">
                                                                    {group.name}
                                                                </h4>
                                                                <div className="space-y-1 text-xs text-surface-onVariant">
                                                                    <p className="flex items-center gap-1.5">
                                                                        <Calendar style={{ fontSize: 14 }} className="text-primary" />
                                                                        <span>{dayName} {timeStr}</span>
                                                                    </p>
                                                                    <p className="flex items-center gap-1.5">
                                                                        <Users style={{ fontSize: 14 }} className="text-primary" />
                                                                        <span>{ageInfo.label} ({group.genderFocus || 'gemischt'})</span>
                                                                    </p>
                                                                </div>
                                                                {(group.altersstufe || '').toLowerCase().includes('sonstig') && (
                                                                    <div className={`mt-2.5 p-2 rounded-xl text-[11px] leading-snug flex items-start gap-1.5 ${
                                                                        isSelected
                                                                            ? 'bg-primary-container/20 text-surface-on border border-primary/30'
                                                                            : 'bg-surface-containerHighest text-surface-on border border-outlineVariant/50'
                                                                    }`}>
                                                                        <Info style={{ fontSize: 14 }} className={`mt-0.5 shrink-0 ${isSelected ? 'text-primary' : 'text-surface-onVariant'}`} />
                                                                        <div className="space-y-1.5 flex-grow">
                                                                            <span><strong>Hinweis:</strong> Diese Gruppe nimmt möglicherweise nicht alle Altersgruppen auf.</span>
                                                                            <button
                                                                                type="button"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setIsWaitlistRequested(true);
                                                                                    if (!preferredGroupIds.includes(group._id)) {
                                                                                        setPreferredGroupIds([...preferredGroupIds, group._id]);
                                                                                    }
                                                                                }}
                                                                                className="block text-xs font-bold text-primary underline hover:opacity-80 text-left"
                                                                            >
                                                                                {isWaitlistRequested && preferredGroupIds.includes(group._id)
                                                                                    ? '✓ Für Warteliste dieser Gruppe vermerkt'
                                                                                    : 'Auf die Warteliste für diese Gruppe setzen'}
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="pt-3 mt-3 border-t border-outlineVariant/20 flex items-center justify-between text-xs font-semibold">
                                                                <span className={isSelected ? 'text-primary font-bold' : 'text-surface-onVariant'}>
                                                                    {isSelected ? 'Als Favorit gewählt' : 'Als Favorit vormerken'}
                                                                </span>
                                                                <div className={`w-5 h-5 rounded-md border flex items-center justify-center ${
                                                                    isSelected ? 'bg-primary border-primary text-primary-on' : 'border-outlineVariant'
                                                                }`}>
                                                                    {isSelected && <CheckIcon style={{ fontSize: 14 }} />}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ) : (
                                        /* Keine passende Gruppe vorhanden -> Automatische Warteliste für Neugründung */
                                        <div className="p-6 rounded-2xl bg-surface-containerLow border border-outlineVariant text-center space-y-3">
                                            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
                                                <Users style={{ fontSize: 24 }} />
                                            </div>
                                            <h4 className="text-title-medium font-bold text-surface-on">
                                                Warteliste für Neugründung aktiv
                                            </h4>
                                            <p className="text-sm text-surface-onVariant max-w-md mx-auto leading-relaxed">
                                                Für das Alter deines Kindes ({age} Jahre) gibt es aktuell noch keine passende aktive Gruppe.
                                                Wir sammeln interessierte Kinder, um gemeinsam eine <strong>neue Gruppe zu gründen</strong>, sobald genügend Kinder und Leitungen zur Verfügung stehen.
                                            </p>
                                            <p className="text-xs text-primary font-semibold">
                                                Mit deiner Anfrage landet dein Kind automatisch auf der Interessentenliste für den nächsten Gruppenstart!
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* SCHRITT 3: Kontaktdaten & Optionale Angaben */}
                            {step === 3 && (
                                <div className="space-y-5 animate-fade-in">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-label-medium font-semibold text-surface-on mb-1.5">
                                                Name des Erziehungsberechtigten *
                                            </label>
                                            <input
                                                type="text"
                                                required
                                                value={parentName}
                                                onChange={(e) => setParentName(e.target.value)}
                                                placeholder="Vor- und Nachname"
                                                className="w-full px-4 py-2.5 rounded-xl border border-outlineVariant bg-surface-containerLowest text-surface-on focus:border-primary focus:outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-label-medium font-semibold text-surface-on mb-1.5">
                                                E-Mail-Adresse *
                                            </label>
                                            <input
                                                type="email"
                                                required
                                                value={parentEmail}
                                                onChange={(e) => setParentEmail(e.target.value)}
                                                placeholder="eltern@beispiel.de"
                                                className="w-full px-4 py-2.5 rounded-xl border border-outlineVariant bg-surface-containerLowest text-surface-on focus:border-primary focus:outline-none"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-label-medium font-semibold text-surface-on mb-1.5">
                                            Telefonnummer (für Rückfragen)
                                        </label>
                                        <input
                                            type="tel"
                                            value={parentPhone}
                                            onChange={(e) => setParentPhone(e.target.value)}
                                            placeholder="+49 170 1234567"
                                            className="w-full px-4 py-2.5 rounded-xl border border-outlineVariant bg-surface-containerLowest text-surface-on focus:border-primary focus:outline-none"
                                        />
                                    </div>

                                    {/* Optionale Adressdaten für zukünftige Fahrgemeinschaften */}
                                    <div className="pt-2 border-t border-outlineVariant/30">
                                        <div className="mb-3">
                                            <span className="text-label-medium font-semibold text-surface-on block">
                                                Wohnort / Adresse (Optional)
                                            </span>
                                            <span className="text-xs text-surface-onVariant">
                                                Hilft uns bei der Planung künftiger Fahrgemeinschaften zur Gruppenstunde.
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-3 gap-3 mb-3">
                                            <div className="col-span-2">
                                                <input
                                                    type="text"
                                                    value={street}
                                                    onChange={(e) => setStreet(e.target.value)}
                                                    placeholder="Straße"
                                                    className="w-full px-4 py-2 rounded-xl border border-outlineVariant bg-surface-containerLowest text-surface-on text-sm focus:border-primary focus:outline-none"
                                                />
                                            </div>
                                            <div>
                                                <input
                                                    type="text"
                                                    value={houseNumber}
                                                    onChange={(e) => setHouseNumber(e.target.value)}
                                                    placeholder="Hausnr."
                                                    className="w-full px-4 py-2 rounded-xl border border-outlineVariant bg-surface-containerLowest text-surface-on text-sm focus:border-primary focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-3 gap-3">
                                            <div>
                                                <input
                                                    type="text"
                                                    value={zipCode}
                                                    onChange={(e) => setZipCode(e.target.value)}
                                                    placeholder="PLZ"
                                                    className="w-full px-4 py-2 rounded-xl border border-outlineVariant bg-surface-containerLowest text-surface-on text-sm focus:border-primary focus:outline-none"
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <input
                                                    type="text"
                                                    value={city}
                                                    onChange={(e) => setCity(e.target.value)}
                                                    placeholder="Ort"
                                                    className="w-full px-4 py-2 rounded-xl border border-outlineVariant bg-surface-containerLowest text-surface-on text-sm focus:border-primary focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-label-medium font-semibold text-surface-on mb-1.5">
                                            Anmerkungen / Wünsche (Optional)
                                        </label>
                                        <textarea
                                            rows="2"
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            placeholder="z. B. Freunde, die auch dabei sind, Vorkenntnisse oder Fragen..."
                                            className="w-full px-4 py-2 rounded-xl border border-outlineVariant bg-surface-containerLowest text-surface-on text-sm focus:border-primary focus:outline-none"
                                        />
                                    </div>

                                    {submitError && (
                                        <div className="p-3 rounded-xl bg-error-container text-error-onContainer flex items-center gap-2 text-sm">
                                            <AlertCircleIcon style={{ fontSize: 18 }} />
                                            <span>{submitError}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Footer Buttons */}
                            <div className="pt-4 border-t border-outlineVariant/30 flex items-center justify-between">
                                {step > 1 ? (
                                    <button
                                        type="button"
                                        onClick={() => setStep(prev => prev - 1)}
                                        className="md3-btn-outlined px-5 py-2 rounded-full flex items-center gap-2 text-sm"
                                    >
                                        <ArrowLeft style={{ fontSize: 18 }} />
                                        <span>Zurück</span>
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={resetAndClose}
                                        className="md3-btn-text text-sm text-surface-onVariant"
                                    >
                                        Abbrechen
                                    </button>
                                )}

                                {step < 3 ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (step === 1 && (!childFirstName.trim() || !childLastName.trim() || !age)) {
                                                alert('Bitte fülle Vorname, Nachname und Alter des Kindes aus.');
                                                return;
                                            }
                                            setStep(prev => prev + 1);
                                        }}
                                        className="md3-btn-filled px-6 py-2.5 rounded-full flex items-center gap-2 text-sm"
                                    >
                                        <span>Weiter</span>
                                        <ArrowRight style={{ fontSize: 18 }} />
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="md3-btn-filled px-6 py-2.5 rounded-full flex items-center gap-2 text-sm disabled:opacity-50"
                                    >
                                        {submitting ? 'Wird übermittelt...' : 'Unverbindliche Anfrage absenden'}
                                        <CheckIcon style={{ fontSize: 18 }} />
                                    </button>
                                )}
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GroupInquiryWizard;
